import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import { useToast } from '../../../hooks/useToast';
import {
  Video,
  Youtube,
  Save,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  ShieldAlert,
  Loader2,
  RefreshCw,
  Info,
  Clock,
  UserCheck
} from 'lucide-react';

const VendorOnboardingTab = () => {
  const { roles } = useAuth();
  const { toast } = useToast();

  const isSuperAdminOrAdmin =
    roles && (roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN'));

  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [activeData, setActiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewEmbedUrl, setPreviewEmbedUrl] = useState('');
  const [urlError, setUrlError] = useState('');

  // Helper to extract YouTube Video ID for embedded preview
  const parseYouTubeEmbedUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();

    // Standard watch URL: youtube.com/watch?v=ID or shortener link
    let match = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/i);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return '';
  };

  const fetchOnboardingVideoSettings = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getWelcomeYoutubeUrl();
      if (res && res.youtubeUrl) {
        setYoutubeUrl(res.youtubeUrl);
        setActiveData(res);
        setPreviewEmbedUrl(parseYouTubeEmbedUrl(res.youtubeUrl));
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load Vendor Onboarding video settings.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isSuperAdminOrAdmin) {
      fetchOnboardingVideoSettings();
    }
  }, [isSuperAdminOrAdmin, fetchOnboardingVideoSettings]);

  const handleUrlChange = (e) => {
    const val = e.target.value;
    setYoutubeUrl(val);

    if (!val.trim()) {
      setUrlError('YouTube URL is required.');
      setPreviewEmbedUrl('');
      return;
    }

    const embedUrl = parseYouTubeEmbedUrl(val);
    if (!embedUrl) {
      setUrlError('Invalid YouTube link. Please enter a valid YouTube URL (e.g. https://www.youtube.com/watch?v=... or https://youtu.be/...)');
      setPreviewEmbedUrl('');
    } else {
      setUrlError('');
      setPreviewEmbedUrl(embedUrl);
    }
  };

  const handleSaveVideoUrl = async (e) => {
    e.preventDefault();
    if (!youtubeUrl.trim()) {
      setUrlError('YouTube URL cannot be empty.');
      return;
    }

    const embedUrl = parseYouTubeEmbedUrl(youtubeUrl);
    if (!embedUrl) {
      setUrlError('Please fix the YouTube link format before saving.');
      return;
    }

    setSaving(true);
    try {
      const res = await vendorService.updateWelcomeYoutubeUrl(youtubeUrl.trim());
      toast.success(res.message || 'Vendor onboarding welcome video link updated successfully.');
      setActiveData(res);
      fetchOnboardingVideoSettings();
    } catch (err) {
      toast.error(err.message || 'Failed to update YouTube video link.');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdminOrAdmin) {
    return (
      <div className="p-8 bg-red-50 rounded-2xl border border-red-200 text-center max-w-2xl mx-auto my-12 shadow-sm">
        <ShieldAlert className="w-14 h-14 text-red-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-red-800 mb-2">Access Restricted</h2>
        <p className="text-red-600 text-sm font-medium">
          The <strong>Vendor Onboarding</strong> management portal is restricted exclusively to Super Admin and Admin roles.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-200/70">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
              Vendor Welcome Guide Video
            </h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/70">
              <Youtube className="w-3.5 h-3.5 mr-1 text-red-600" /> WhatsApp Onboarding
            </span>
          </div>
          <p className="text-xs md:text-sm text-slate-500 max-w-2xl">
            Configure the introductory video link sent automatically to newly registered vendors via WhatsApp.
          </p>
        </div>

        {activeData && (
          <div className="shrink-0">
            {activeData.isCustom ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Custom Link Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                <span className="w-2 h-2 rounded-full bg-slate-400"></span> System Default Link
              </span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border border-slate-200/80 shadow-sm min-h-[260px]">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
          <p className="text-slate-500 text-sm font-medium">Loading onboarding video configuration...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Form & Configuration Area */}
          <div className="lg:col-span-7 space-y-6">
            <form onSubmit={handleSaveVideoUrl} className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <Youtube className="w-4.5 h-4.5 text-red-600" />
                    YouTube Video Link <span className="text-red-500">*</span>
                  </label>
                  {activeData?.isCustom && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Saved
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Paste full URL (e.g. <code className="bg-slate-100 text-indigo-700 px-1.5 py-0.5 rounded font-mono text-xs">https://www.youtube.com/watch?v=...</code> or <code className="bg-slate-100 text-indigo-700 px-1.5 py-0.5 rounded font-mono text-xs">https://youtu.be/...</code>).
                </p>

                <div className="relative flex items-center">
                  <Youtube className="w-5 h-5 text-slate-400 absolute left-3.5 pointer-events-none" />
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={handleUrlChange}
                    placeholder="https://www.youtube.com/watch?v=YOUR_VIDEO_ID"
                    required
                    className={`w-full pl-11 pr-20 py-3 rounded-xl border text-sm font-medium transition-all focus:outline-none focus:ring-2 ${
                      urlError
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-100 text-red-900 bg-red-50/20'
                        : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100 text-slate-900 bg-white'
                    }`}
                  />
                  {youtubeUrl && (
                    <button
                      type="button"
                      onClick={() => {
                        setYoutubeUrl('');
                        setPreviewEmbedUrl('');
                        setUrlError('');
                      }}
                      className="absolute right-3 text-xs font-semibold px-2.5 py-1 text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {urlError && (
                  <p className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    {urlError}
                  </p>
                )}
              </div>

              {/* Status & Last Updated Details */}
              {activeData && (
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 text-xs space-y-2.5">
                  <div className="flex items-center justify-between text-slate-600">
                    <span className="font-semibold text-slate-500 flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-slate-400" /> Mode:
                    </span>
                    <span className="font-bold text-slate-800">
                      {activeData.isCustom ? 'Active Custom Link' : 'System Default Link'}
                    </span>
                  </div>
                  {activeData.updatedAt && (
                    <div className="flex items-center justify-between text-slate-600">
                      <span className="font-semibold text-slate-500 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-slate-400" /> Last Updated:
                      </span>
                      <span className="font-medium text-slate-700">
                        {new Date(activeData.updatedAt).toLocaleString('en-IN', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })}
                      </span>
                    </div>
                  )}
                  {activeData.updatedBy && (
                    <div className="flex items-center justify-between text-slate-600">
                      <span className="font-semibold text-slate-500 flex items-center gap-1.5">
                        <UserCheck className="w-3.5 h-3.5 text-slate-400" /> Updated By:
                      </span>
                      <span className="font-medium text-slate-700">{activeData.updatedBy}</span>
                    </div>
                  )}
                </div>
              )}

              {/* High Contrast Primary Action Buttons */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={saving || !!urlError || !youtubeUrl.trim()}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-sm rounded-xl shadow-sm hover:shadow transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving Link...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Video Link
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={fetchOnboardingVideoSettings}
                  disabled={saving}
                  className="p-3 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 font-semibold text-sm rounded-xl transition-all border border-slate-200"
                  title="Reload settings"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Information Card */}
            <div className="bg-indigo-50/60 rounded-2xl p-5 border border-indigo-100 space-y-2.5">
              <h3 className="text-xs font-bold text-indigo-950 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-indigo-600" />
                WhatsApp Onboarding Flow Information
              </h3>
              <ul className="text-xs text-indigo-900/80 space-y-1.5 leading-relaxed list-disc list-inside">
                <li>This YouTube video link is automatically attached to parameter <code>{`{{2}}`}</code> in the WhatsApp template <code>user_signup_welcome</code>.</li>
                <li>When a new vendor signs up on Top Interiors India, they instantly receive the welcome message along with this YouTube video link on WhatsApp.</li>
                <li>Updating the YouTube video URL here is instantaneous and <strong>does not require any template re-approval in Yeti Marketing</strong>.</li>
              </ul>
            </div>
          </div>

          {/* Live Video Preview Column */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Video className="w-4 h-4 text-indigo-600" />
                  Live Player Preview
                </h3>
                {youtubeUrl && (
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-bold flex items-center gap-1"
                  >
                    Open in YouTube <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {previewEmbedUrl ? (
                <div className="space-y-2.5">
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-950 shadow-md border border-slate-200">
                    <iframe
                      src={previewEmbedUrl}
                      title="Vendor Welcome Guide Preview"
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <p className="text-xs text-center text-slate-500 font-medium">
                    This is how the video appears to vendors when opening the YouTube link.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-xl border border-dashed border-slate-300 text-center min-h-[230px]">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3 text-indigo-600">
                    <Youtube className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-bold text-slate-700">No Valid Preview</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">
                    Paste a valid YouTube link on the left to see the live video preview here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorOnboardingTab;
